import React from "react";
import * as styles from "./ServiceRow.module.scss";
import { ResponsiveSanityImage } from "@silversea-ssc/ui-components-react";
import Slideshow, { SlideshowItem } from "../Slideshow/Slideshow";
import classNames from "classnames/bind";
import { ComponentBackgroundColor } from "types/componentBackground";
import { stringPresent } from "../../utils/strings";
import loadable from "@loadable/component";
import { convertImages } from "components/Slideshow/utils";
import { isPresent } from "src/utils/checks";
import { SanityImageRatio } from "@silversea-ssc/ui-components-react";

const SlideshowVideo = loadable(
  () => import("components/Slideshow/SlideshowVideo")
);

const cx = classNames.bind(styles);

export interface ServiceRowProps {
  variant: "right" | "left";
  backgroundColor?: ComponentBackgroundColor;
  className?: string;
}

interface ServiceRowTitleProps {
  tagline?: string;
}

export const ServiceRowTitle: React.FC<ServiceRowTitleProps> = ({
  children,
  tagline
}) => (
  <div className={styles.title}>
    {stringPresent(tagline) && <h4 className={styles.tagline}>{tagline}</h4>}
    <h3>{children}</h3>
  </div>
);

export const ServiceRowContent: React.FC = ({ children }) => (
  <div className={styles.content}>{children}</div>
);

export const ServiceRowFooter: React.FC = ({ children }) => (
  <div className={styles.footer}>{children}</div>
);

const InternalImage: React.FC<{ item: SlideshowItem }> = ({ item }) => {
  if (item.__typename == "SanityVideoWithPosterImage") {
    const videoProps = convertImages([item], false)[0];
    if (!videoProps) return null;
    return <SlideshowVideo {...videoProps} />;
  } else {
    return (
      <SanityImageRatio ratio="auto" image={item}>
        <ResponsiveSanityImage
          image={item}
          defaultWidth={800}
          defaultHeight={550}
        />
      </SanityImageRatio>
    );
  }
};

export const ServiceRowImages: React.FC<{
  items: (SlideshowItem | undefined)[];
}> = ({ items }) => {
  const filteredItems = items.filter(isPresent);

  return (
    <div className={styles.images}>
      {filteredItems.length == 1 && <InternalImage item={filteredItems[0]} />}
      {filteredItems.length > 1 && (
        <Slideshow items={filteredItems} hideCaption fullscreen={false} />
      )}
    </div>
  );
};

const ServiceRow: React.FC<ServiceRowProps> = ({
  children,
  variant = "right",
  backgroundColor = "neutral05",
  className = undefined
}) => {
  let hasFooter = false;
  if (children) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    React.Children.forEach(children, (child: any) => {
      if (child && "type" in child && child?.type == ServiceRowFooter)
        hasFooter = true;
    });
  }

  return (
    <div
      className={cx({
        serviceRow: true,
        variantLeft: variant == "left",
        backgroundWhite: backgroundColor == "white",
        backgroundGray: backgroundColor == "neutral05",
        withFooter: hasFooter,
        [className || ""]: className
      })}
    >
      <div className={styles.card}>{children}</div>
    </div>
  );
};

export default ServiceRow;
